import { collection, getDocs, addDoc, query } from 'firebase/firestore';
import { db } from '../firebase/firebase';

/**
 * Creates a multilingual menu collection in Firestore
 * This script should be run once to initialize the multilingual menu data
 * It will fetch the existing menu data and create a new collection with multilingual support
 * @returns {Promise<{success: boolean, message: string}>} Result object with success status and message
 */
const createMultilingualMenu = async () => {
  try {
    console.log('Starting to create multilingual menu data...');

    // Check if menu_translations collection already exists and has data
    const translationsQuery = query(collection(db, 'menu_translations'));
    const translationsSnapshot = await getDocs(translationsQuery);

    if (!translationsSnapshot.empty) {
      console.log('Multilingual menu data already exists. No action taken.');
      return {
        success: true,
        message: 'Multilingual menu data already exists. No action taken.'
      };
    }

    // Fetch existing menu data from menu or menu_2025Now collection
    let menuData = null;
    let menuSource = '';

    // Try menu_2025Now first
    const menu2025Query = query(collection(db, 'menu_2025Now'));
    const menu2025Snapshot = await getDocs(menu2025Query);

    if (!menu2025Snapshot.empty) {
      menuData = menu2025Snapshot.docs[0].data();
      menuSource = 'menu_2025Now';
    } else {
      // Fall back to regular menu collection
      const menuQuery = query(collection(db, 'menu'));
      const menuSnapshot = await getDocs(menuQuery);

      if (!menuSnapshot.empty) {
        menuData = menuSnapshot.docs[0].data();
        menuSource = 'menu';
      }
    }

    if (!menuData) {
      console.log('No menu data found in any collection. Cannot create multilingual menu.');
      return {
        success: false,
        message: 'No menu data found in any collection. Cannot create multilingual menu.'
      };
    }

    console.log(`Found menu data in ${menuSource} collection. Creating multilingual version...`);

    // Create multilingual menu data structure
    const multilingualMenu = {
      // Keep original metadata
      originalTitle: menuData.title || 'Le Menu',
      originalSubtitle: menuData.subtitle || '',
      originalDate: menuData.date || '',
      originalFooter: menuData.footer || '',

      // Add language-specific metadata
      translations: {
        en: {
          title: menuData.title || 'The Menu',
          subtitle: menuData.subtitle || '',
          date: menuData.date || '',
          footer: menuData.footer || ''
        },
        fr: {
          title: menuData.title || 'Le Menu',
          subtitle: menuData.subtitle || '',
          date: menuData.date || '',
          footer: menuData.footer || ''
        },
        es: {
          title: menuData.title || 'El Menú',
          subtitle: menuData.subtitle || '',
          date: menuData.date || '',
          footer: menuData.footer || ''
        },
        tr: {
          title: menuData.title || 'Menü',
          subtitle: menuData.subtitle || '',
          date: menuData.date || '',
          footer: menuData.footer || ''
        }
      },

      // Transform sections to support multilingual content
      sections: menuData.sections.map(section => ({
        id: section.title.toLowerCase().replace(/[^\w]+/g, '-'),
        translations: {
          en: {
            title: section.title
          },
          fr: {
            title: section.title
          },
          es: {
            title: section.title
          },
          tr: {
            title: section.title
          }
        },
        courses: section.courses.map(course => ({
          id: course.id,
          translations: {
            en: {
              name: course.name,
              description: course.description
            },
            fr: {
              name: course.name,
              description: course.description
            },
            es: {
              name: course.name,
              description: course.description
            },
            tr: {
              name: course.name,
              description: course.description
            }
          }
        }))
      }))
    };

    // Add the multilingual menu data to Firestore
    const menuRef = await addDoc(collection(db, 'menu_translations'), multilingualMenu);
    console.log(`Multilingual menu data added with ID: ${menuRef.id}`);

    return {
      success: true,
      message: `Multilingual menu data created successfully from ${menuSource} collection.`
    };
  } catch (error) {
    console.error('Error creating multilingual menu:', error);
    return {
      success: false,
      message: `Error creating multilingual menu: ${error.message}`
    };
  }
};

export default createMultilingualMenu;
